import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { 
  buildTemplateVariables, 
  processTemplate, 
  campaignDataToPreviewData,
  type TemplateType,
  type CampaignPreviewData 
} from '@/utils/emailPreviewUtils';
import type { EmailTemplate } from '@/hooks/useEmailTemplates';

interface TargetedUser {
  user_id: string;
  business_id?: string;
  user_email: string;
  user_name: string;
  business_name?: string;
  created_at: string;
}

interface UseCampaignEmailPreviewProps {
  templateType: TemplateType;
  targetedUsers: TargetedUser[];
  selectedRecipients: string[];
  selectedPreviewUserId?: string;
}

export function useCampaignEmailPreview({
  templateType,
  targetedUsers,
  selectedRecipients,
  selectedPreviewUserId
}: UseCampaignEmailPreviewProps) {
  const { currentCommunity } = useCommunity();

  // Fetch the real email template from the database
  const { data: emailTemplate, isLoading: templateLoading } = useQuery({
    queryKey: ['email-template', currentCommunity?.id, templateType],
    queryFn: async () => {
      if (!currentCommunity?.id) return null;
      
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('community_id', currentCommunity.id)
        .eq('template_type', templateType)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching email template:', error);
        return null;
      }
      
      return data as EmailTemplate;
    },
    enabled: !!currentCommunity?.id && !!templateType
  });

  // Get preview recipients (only selected recipients, max 3)
  const previewRecipients = useMemo(() => {
    if (!targetedUsers?.length || !selectedRecipients?.length) return [];
    
    // Filter targeted users to only include selected recipients
    const selectedUsers = targetedUsers.filter(user => 
      selectedRecipients.includes(user.user_id)
    );
    
    // Take max 3 recipients for preview dropdown
    return selectedUsers.slice(0, 3);
  }, [targetedUsers, selectedRecipients]);

  // Get selected recipient for preview
  const selectedRecipient = useMemo(() => {
    if (!selectedPreviewUserId || !targetedUsers?.length) return null;
    
    return targetedUsers.find(user => user.user_id === selectedPreviewUserId) || null;
  }, [selectedPreviewUserId, targetedUsers]);

  // Process email template with selected recipient data
  const processedEmail = useMemo(() => {
    if (!emailTemplate || !selectedRecipient || !currentCommunity) {
      return { subject: '', html: '', text: '' };
    }

    // Convert campaign recipient data to preview data format
    const campaignData: CampaignPreviewData = {
      user_id: selectedRecipient.user_id,
      business_id: selectedRecipient.business_id,
      user_email: selectedRecipient.user_email,
      user_name: selectedRecipient.user_name,
      business_name: selectedRecipient.business_name,
      created_at: selectedRecipient.created_at
    };

    const previewData = campaignDataToPreviewData(
      campaignData,
      currentCommunity.name,
      currentCommunity.primary_color || '#000000',
      currentCommunity.community_app_url || 'https://app.communitly.com',
      currentCommunity.email_domain || 'community.com',
      currentCommunity.admin_contact_email || 'support@communitly.com'
    );

    // Build template variables using existing logic
    const variables = buildTemplateVariables(templateType, previewData);

    // Process templates with variables
    return {
      subject: processTemplate(emailTemplate.subject_template, variables),
      html: processTemplate(emailTemplate.html_template, variables),
      text: emailTemplate.text_template ? processTemplate(emailTemplate.text_template, variables) : '',
    };
  }, [emailTemplate, selectedRecipient, currentCommunity, templateType]);

  return {
    emailTemplate,
    templateLoading,
    previewRecipients,
    selectedRecipient,
    processedEmail,
    isReady: !!emailTemplate && !!selectedRecipient
  };
}